import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';

import { CropData, FileItem, FileResult, NotificationData } from '../../../domain/public-api';

import {
  cancelImageEdit,
  confirmUpload,
  fileRetry,
  prepareFilesFromComputer,
  removeAllFiles,
  removeFile,
  returnFiles,
  saveImageEdit,
  triggerNotifications,
} from './files.actions';
import {
  getFileItems,
  getFileToEdit,
  getHasFileItems,
  getIsUploadConfirmed,
} from './files.selectors';

@Injectable({ providedIn: 'root' })
export class FilesFacade {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  cancelImageEdit(): void {
    this.store.dispatch(cancelImageEdit());
  }

  confirmUpload(): void {
    this.store.dispatch(confirmUpload());
  }

  getFileItems(): Observable<FileItem[]> {
    return this.store.select(getFileItems);
  }

  getFileToEdit(): Observable<FileItem | undefined> {
    return this.store.select(getFileToEdit);
  }

  getHasFileItems(): Observable<boolean> {
    return this.store.select(getHasFileItems);
  }

  getHasFilesSelected(): Observable<boolean> {
    return this.store.select(getFileItems).pipe(map((items) => items && items.length > 0));
  }

  getIsUploadConfirmed(): Observable<boolean> {
    return this.store.select(getIsUploadConfirmed);
  }

  prepareFilesFromComputer(files: File[]): void {
    this.store.dispatch(prepareFilesFromComputer({ files }));
  }

  retryFile(fileId: number): void {
    this.store.dispatch(fileRetry({ fileId }));
  }

  removeFile(fileId: number): void {
    this.store.dispatch(removeFile({ fileId }));
  }

  removeAllFiles(): void {
    this.store.dispatch(removeAllFiles());
  }

  /**
   * Sets the crop data for the currently edited image.
   */
  setImageCropData(cropData: CropData): void {
    this.store.dispatch(saveImageEdit({ cropData }));
  }

  watchForUploadedFiles(): Observable<FileResult[]> {
    return this.actions$.pipe(
      ofType(returnFiles),
      map(({ fileResults }) => fileResults),
    );
  }

  watchNotifications(): Observable<NotificationData[]> {
    return this.actions$.pipe(
      ofType(triggerNotifications),
      map(({ notifications }) => notifications),
    );
  }
}
